<template>
    <div>
        <h3 class="is-size-4 pt-5 pb-3 has-text-weight-bold">
            SALDOS DE CUENTAS BANCARIAS
        </h3>
        <div
            v-if="accountsList.length === 0"
            class="has-text-centered mt-6 mb-5"
        >
            <p class="has-text-weight-bold">
                Aquí podrás ver el detalle de tus saldos de cuentas
            </p>
            <p>
                Ingresa tus cuentas de forma
                <a
                    v-on:click.prevent="toManualTab"
                    class="has-text-flow is-underlined"
                    type="is-text"
                    >manual</a
                >
                o
                <a
                    v-on:click.prevent="toAutomaticTab"
                    class="has-text-flow is-underlined"
                    type="is-text"
                    >automática</a
                >
            </p>
        </div>
        <ol id="bankList">
            <li
                class="py-5 listItem is-relative"
                v-for="(account, accIndex) in accountsList"
                :key="account.id"
            >
                <b-loading
                    :is-full-page="false"
                    v-model="isLoading"
                    v-if="itemLoading === accIndex"
                ></b-loading>
                <div
                    class="is-flex is-justify-content-space-between is-align-items-center"
                >
                    <div>
                        <div
                            class="is-flex is-justify-content-flex-start is-align-items-center"
                        >
                            <p
                                class="is-size-7 mr-5"
                                v-if="!account.is_automatic"
                            >
                                Ingreso manual
                            </p>
                            <FintocLogo
                                class="mr-5 is-relative"
                                style="width:72px;top:2px"
                                v-if="account.is_automatic"
                            />
                            <p
                                class="is-size-7 mr-5"
                                v-if="account.is_password_changed"
                            >
                                (CLAVE DE BANCO CAMBIADA)
                            </p>
                            <b-tag
                                type="is-flow is-outlined"
                                v-if="accIndex === 0 && showNewAccountTag"
                                rounded
                                >Cuenta nueva</b-tag
                            >
                        </div>
                        <p class="is-size-4 has-text-weight-bold">
                            {{ account.financial_entity.name }}
                        </p>
                        <p
                            v-if="account.number"
                            class="is-size-7 has-text-weight-semibold is-family-monospace"
                        >
                            {{ account.account_type }} número:
                            {{ account.number }}
                        </p>
                        <p v-if="!account.is_automatic" class="is-size-7">
                            Actualizado al día
                            {{
                                moment(account.updated_at).format(
                                    "D [de] MMMM YYYY"
                                )
                            }}
                        </p>
                        <p v-if="account.is_automatic" class="is-size-7">
                            Los saldos pueden demorar hasta una hora en verse
                            reflejados
                        </p>
                    </div>
                    <div class="is-flex is-align-items-center">
                        <p
                            v-if="editAmount !== accIndex"
                            class="is-size-5 has-text-weight-bold"
                        >
                            {{ toMoney(account.amount) }}
                        </p>
                        <AmountEdit
                            :amount="account.amount"
                            v-if="editAmount === accIndex"
                            @on-update-manual-amount="
                                (newValue) =>
                                    onUpdateManualAmount(newValue, accIndex)
                            "
                            @on-cancel-manual-amount="onCancelManualAmount"
                        />
                        <DropdownMenu
                            :disabled="isLoading"
                            v-if="editAmount !== accIndex"
                            :itemIndex="accIndex"
                            @on-edit="onEdit"
                            @on-show-confirm="onShowConfirm"
                            :isAutomatic="account.is_automatic"
                        />
                    </div>
                </div>
                <ConfirmMsg
                    @on-cancel="onCancel"
                    @on-confirm-disconnect="onConfirmDisconnect(accIndex)"
                    @on-confirm-delete="onConfirmDelete(accIndex)"
                    v-if="confirmationMsg === accIndex"
                    :isAutomatic="account.is_automatic"
                />
            </li>
        </ol>
    </div>
</template>

<style scoped>
.listItem + .listItem {
    border-top: 1px solid #efefef;
}
</style>

<script>
import moment from "moment";
import FintocLogo from "@/components/svg/FintocLogo.vue";
import DropdownMenu from "./dropdownMenu";
import ConfirmMsg from "./confirmMsg";
import AmountEdit from "./amountEdit";
import { parseToMoney } from "@/utils/currency";

export default {
    name: "BankList",
    data() {
        return {
            confirmationMsg: null,
            editAmount: null,
            editField: null,
        };
    },
    components: {
        FintocLogo,
        DropdownMenu,
        ConfirmMsg,
        AmountEdit,
    },
    props: {
        accountsList: {
            type: Array,
            default: function() {
                return [];
            },
        },
        changeTab: {
            type: Function,
            default: function() {},
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        showNewAccountTag: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        toMoney: parseToMoney,
        toManualTab: function() {
            this.changeTab("manual");
        },
        toAutomaticTab: function() {
            this.changeTab("automatic");
        },
        onShowConfirm: function(index) {
            this.editAmount = null;
            this.confirmationMsg = index;
        },
        onCancel: function() {
            this.confirmationMsg = null;
        },
        onCancelManualAmount: function() {
            this.editAmount = null;
        },
        onEdit: function(index) {
            this.confirmationMsg = null;
            this.editAmount = index;
        },
        moment(date) {
            moment.locale("es");
            return moment(date);
        },
        // emited methods
        onUpdateManualAmount: function(newValue, index) {
            this.$emit("on-update-manual-amount", {
                id: this.accountsList[index].id,
                field: {
                    amount: newValue,
                    financial_entity: this.accountsList[index].financial_entity,
                },
            });
        },
        onConfirmDisconnect: function(index) {
            this.$emit(
                "on-confirm-disconnect",
                this.accountsList[index].company_product_link_id,
            );
        },
        onConfirmDelete: function(index) {
            this.$emit("on-confirm-delete", this.accountsList[index].id);
        },
    },
    watch: {
        itemLoading: function() {
            if (!this.isLoading) {
                this.confirmationMsg = null;
                this.editAmount = null;
            }
        },
    },
    computed: {
        itemLoading: function() {
            if (this.isLoading)
                return this.editAmount === 0 || this.editAmount > 0
                    ? this.editAmount
                    : this.confirmationMsg;
            else return null;
        },
    },
};
</script>
